import { useState, useCallback, useEffect } from "react";
import type { BreadcrumbItem } from "../GoogleDriveBrowser/Breadcrumb";
import type { GoogleDriveFile, GoogleSharedDrive } from "@/lib/hooks/google-drive/use-google-drive-browser";

interface UseGoogleDriveNavigationProps {
  initialDriveId?: string;
  initialParentId?: string;
  initialParentName?: string;
  initialBreadcrumbs?: BreadcrumbItem[];
  onFolderSelect?: (folder: GoogleDriveFile) => void;
}

export function useGoogleDriveNavigation({
  initialDriveId,
  initialParentId,
  initialParentName,
  initialBreadcrumbs,
  onFolderSelect,
}: UseGoogleDriveNavigationProps) {
  const [currentDriveId, setCurrentDriveId] = useState<string | undefined>(initialDriveId);
  const [currentParentId, setCurrentParentId] = useState<string | undefined>(initialParentId);
  const [viewMode, setViewMode] = useState<"drives" | "files">(
    initialParentId ? "files" : "drives"
  );
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);

  useEffect(() => {
    if (breadcrumbs.length === 0) {
      if (initialBreadcrumbs && initialBreadcrumbs.length > 0) {
        setBreadcrumbs(initialBreadcrumbs);
      } else if (initialParentId) {
        setBreadcrumbs([
          { id: initialParentId, name: initialParentName || "Folder", driveId: initialDriveId },
        ]);
      }
    }
  }, [initialParentId, initialParentName, initialDriveId, initialBreadcrumbs, breadcrumbs.length]);

  useEffect(() => {
    if (!initialBreadcrumbs || initialBreadcrumbs.length === 0) return;
    if (breadcrumbs.length === 0 || initialBreadcrumbs.length > breadcrumbs.length) {
      setBreadcrumbs(initialBreadcrumbs);
    }
  }, [initialBreadcrumbs, breadcrumbs.length]);

  const handleDriveSelect = useCallback((drive: GoogleSharedDrive) => {
    setCurrentDriveId(drive.id);
    setCurrentParentId(undefined);
    setViewMode("files");
    setBreadcrumbs([
      { id: "drives", name: "Shared Drives" },
      { id: drive.id, name: drive.name, driveId: drive.id },
    ]);
  }, []);

  const handleFolderSelect = useCallback(
    (folder: GoogleDriveFile) => {
      setCurrentParentId(folder.id);
      setBreadcrumbs((prev) => [
        ...prev,
        { id: folder.id, name: folder.name, driveId: currentDriveId },
      ]);
      onFolderSelect?.(folder);
    },
    [currentDriveId, onFolderSelect]
  );

  const handleBreadcrumbNavigate = useCallback((item: BreadcrumbItem) => {
    if (item.id === "drives") {
      setViewMode("drives");
      setCurrentDriveId(undefined);
      setCurrentParentId(undefined);
      setBreadcrumbs([]);
    } else {
      setCurrentDriveId(item.driveId);
      setCurrentParentId(item.id === item.driveId ? undefined : item.id);
      setBreadcrumbs((prev) => {
        const itemIndex = prev.findIndex((b) => b.id === item.id);
        return itemIndex >= 0 ? prev.slice(0, itemIndex + 1) : prev;
      });
    }
  }, []);

  const goBack = useCallback(() => {
    if (breadcrumbs.length > 1) {
      const parentBreadcrumb = breadcrumbs[breadcrumbs.length - 2];
      handleBreadcrumbNavigate(parentBreadcrumb);
    } else if (breadcrumbs.length === 1) {
      setViewMode("drives");
      setCurrentDriveId(undefined);
      setCurrentParentId(undefined);
      setBreadcrumbs([]);
    } else if (viewMode === "files") {
      setViewMode("drives");
      setCurrentDriveId(undefined);
      setCurrentParentId(undefined);
      setBreadcrumbs([]);
    }
  }, [breadcrumbs, viewMode, handleBreadcrumbNavigate]);

  return {
    currentDriveId,
    setCurrentDriveId,
    currentParentId,
    setCurrentParentId,
    viewMode,
    setViewMode,
    breadcrumbs,
    setBreadcrumbs,
    handleDriveSelect,
    handleFolderSelect,
    handleBreadcrumbNavigate,
    goBack,
  };
}
